<template>
  <div class="animated-heart-container">
    <button
      @click="handleClick"
      class="heart-button"
      :class="{ liked: isLiked, animating: isAnimating }"
      :disabled="isLoading"
    >
      <font-awesome
        :icon="isLiked ? ['fas', 'heart'] : ['far', 'heart']"
        class="heart-icon"
      />
    </button>
    <span v-if="showCount && likesCount > 0" class="likes-count">
      {{ formatCount(likesCount) }}
    </span>
  </div>
</template>

<script setup lang="ts">
interface Props {
  isLiked: boolean;
  likesCount: number;
  showCount?: boolean;
  isLoading?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  showCount: true,
  isLoading: false,
});

const emit = defineEmits<{
  click: [];
}>();

const isAnimating = ref(false);

const handleClick = () => {
  if (props.isLoading) return;
  
  isAnimating.value = true;
  emit("click");
  
  setTimeout(() => {
    isAnimating.value = false;
  }, 300);
};

const formatCount = (count: number): string => {
  if (count < 1000) {
    return count.toString();
  } else if (count < 1000000) {
    return `${(count / 1000).toFixed(1)}K`;
  } else {
    return `${(count / 1000000).toFixed(1)}M`;
  }
};
</script>

<style scoped>
.animated-heart-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.heart-button {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;
  outline: none;
}

.heart-button:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.heart-icon {
  font-size: 24px;
  color: white;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
  transition: color 0.2s ease, transform 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

.heart-button.liked .heart-icon {
  color: #ef4444;
}

.heart-button.animating .heart-icon {
  transform: scale(1.3);
  animation: heartBeat 0.3s ease;
}

@keyframes heartBeat {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.3);
  }
  100% {
    transform: scale(1);
  }
}

.likes-count {
  color: white;
  font-size: 11px;
  font-weight: 600;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
  line-height: 1;
}
</style>

